import React, { useState } from 'react';
import SEO from '../../components/SEO';
import { generateArticle } from '../../utils/structuredData';
import seoData from '../../config/seoData';
import { Phone, MessageCircle, Instagram, Menu, X, ArrowLeft, Calendar, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const LimpezaPosObraResidencial = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Vi o artigo sobre limpeza pós-obra residencial e gostaria de um orçamento.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen
      <SEO 
        title={seoData['/blog/limpeza-pos-obra-residencial'].title}
        description={seoData['/blog/limpeza-pos-obra-residencial'].description}
        canonical={seoData['/blog/limpeza-pos-obra-residencial'].canonical}
      /> bg-white">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-blue-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/blog" className="text-green-400 font-semibold">Blog</Link>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Início</button>
              <button onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Serviços</button>
              <button onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Limpeza Pós-Obra</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400 font-semibold">Blog</button>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Article Content */}
      <article className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <button 
            onClick={() => navigate('/blog')}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para o Blog
          </button>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Limpeza Pós-Obra Residencial: Checklist Completo do que Deve ser Limpo
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Janeiro 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>10 min de leitura</span>
              </div>
            </div>
          </header>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              Acabou a obra ou reforma da sua casa e agora você precisa fazer a <strong>limpeza pós-obra residencial</strong>? Este checklist completo vai te ajudar a não esquecer nenhum detalhe e entregar seu imóvel impecável.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Checklist Completo: Limpeza Pós-Obra Residencial
            </h2>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              1. Pisos e Rodapés
            </h3>
            <Card className="bg-blue-50 border-2 border-blue-200 mb-6">
              <CardContent className="p-6">
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Remover resíduos de cimento, argamassa e rejunte</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Limpar manchas de tinta e massa corrida</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Aspirar pó fino acumulado em cantos</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Lavar pisos com produtos adequados (porcelanato, cerâmica, madeira)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Limpar rodapés por completo</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              2. Paredes e Tetos
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Remover respingos de tinta e massa</li>
              <li>Limpar pó acumulado em superfícies</li>
              <li>Passar pano úmido em paredes pintadas</li>
              <li>Limpar manchas de cola ou silicone</li>
              <li>Verificar e limpar luminárias embutidas</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              3. Janelas, Portas e Esquadrias
            </h3>
            <Card className="bg-green-50 border-2 border-green-200 mb-6">
              <CardContent className="p-6">
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Limpeza completa de vidros (interno e externo)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Limpar trilhos de janelas e portas de correr</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Remover etiquetas e fitas de proteção</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Limpar batentes e marcos</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Polir metais (fechaduras, puxadores, dobradiças)</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              4. Banheiros e Área de Serviço
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Limpar azulejos e rejuntes</li>
              <li>Desinfetar vasos sanitários, pias e ralos</li>
              <li>Remover resíduos de silicone e rejunte</li>
              <li>Polir metais (torneiras, registros, chuveiros)</li>
              <li>Limpar box e espelhos</li>
              <li>Limpar tanque e área de serviço</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              5. Cozinha
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Limpar armários (interno e externo)</li>
              <li>Limpar bancadas e pias</li>
              <li>Desinfetar superfícies</li>
              <li>Limpar azulejos e área da pia</li>
              <li>Polir torneiras e metais</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              6. Áreas Externas (se aplicável)
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Varrer e lavar varandas</li>
              <li>Limpar churrasqueiras</li>
              <li>Lavar áreas de garagem</li>
              <li>Limpar grades e portões</li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Produtos e Equipamentos Necessários
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-3">Produtos de Limpeza:</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Removedor de cimento e argamassa</li>
                <li>Removedor de rejunte</li>
                <li>Limpa vidros profissional</li>
                <li>Desengraxante multiuso</li>
                <li>Desinfetante para áreas molhadas</li>
                <li>Álcool 70% para metais</li>
              </ul>

              <h4 className="font-bold text-gray-900 mb-3 mt-6">Equipamentos:</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Aspirador de pó profissional</li>
                <li>Lavadora de piso (para grandes áreas)</li>
                <li>Espátulas e raspadores</li>
                <li>Panos de microfibra</li>
                <li>Baldes e rodos</li>
              </ul>
            </div>

            <Card className="bg-yellow-50 border-2 border-yellow-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">⚠️ Atenção</h3>
                <p className="text-gray-700">
                  Não use produtos ácidos em pedras naturais (mármore, granito) sem orientação. Podem manchar permanentemente.
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Quanto Tempo Leva?
            </h2>
            <p className="text-gray-700 mb-4">
              O tempo varia conforme o tamanho do imóvel e nível de sujeira:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Apartamento 2 quartos:</strong> 6-8 horas (2 profissionais)</li>
              <li><strong>Casa 3 quartos:</strong> 8-12 horas (2-3 profissionais)</li>
              <li><strong>Sobrado 4 quartos:</strong> 12-16 horas (3-4 profissionais)</li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Vale a Pena Contratar Profissionais?
            </h2>
            <p className="text-gray-700 mb-4">
              Sim! Uma limpeza pós-obra residencial profissional oferece:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Equipamentos industriais que você não tem em casa</li>
              <li>Produtos especializados para cada tipo de sujeira</li>
              <li>Equipe experiente que sabe exatamente o que fazer</li>
              <li>Economia de tempo (você não perde dias fazendo a limpeza)</li>
              <li>Resultado profissional e garantia</li>
            </ul>

            <Card className="bg-blue-900 text-white mb-6">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Precisa de Limpeza Pós-Obra Residencial?</h3>
                <p className="mb-6">
                  A WellTec realiza limpeza completa seguindo este checklist em BH e região
                </p>
                <Button 
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Solicitar Orçamento Gratuito
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Related Articles */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Artigos Relacionados</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/custo-limpeza-pos-obra')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Quanto Custa Limpeza Pós-Obra?</h4>
                  <p className="text-gray-600 text-sm">Guia completo de preços para 2025</p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/remover-cimento-argamassa')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Como Remover Resíduos de Cimento</h4>
                  <p className="text-gray-600 text-sm">Técnicas profissionais para remover cimento e argamassa</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="font-bold mb-4">WellTec</h3>
              <p className="text-gray-400 text-sm">Limpeza pós-obra profissional em BH e região</p>
            </div>
            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400">Limpeza Pós-Obra</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400">Locação de Equipamentos</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="tel:+5531971823249" className="hover:text-green-400">(31) 97182-3249</a></li>
                <li><button onClick={handleWhatsAppClick} className="hover:text-green-400">WhatsApp</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} WellTec. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      <button 
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default LimpezaPosObraResidencial;
